// Read-only accessors for the guided_report snapshot persisted by the backend
// at the report top level (json_params.guided_report). The report arrives as the
// plain object the reports panel keeps on each list item under `current`.
type AnyReport = Record<string, unknown> | null | undefined

// Note: the report name is NOT part of the guided_report snapshot — the backend
// (Api::Skipper::Report::Create#build_guided_snapshot) persists only resolved_report_spec/
// narrative_md/state_md/targeting/generation_id/session/plan/display_md. The display
// name comes from the StatReport title, passed in separately by callers.
export interface ResolvedSpec {
  objectives: string[]
  researchQuestions: string[]
  kpis: string[]
  confidenceLevel: number | null
  methodology: string | null
}

const EMPTY: ResolvedSpec = {
  objectives: [],
  researchQuestions: [],
  kpis: [],
  confidenceLevel: null,
  methodology: null
}

const toArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : [])

const asRecord = (v: unknown): Record<string, unknown> =>
  v && 'object' == typeof v ? (v as Record<string, unknown>) : {}

// Every Skipper Report, whatever its provenance and whether or not it kept a snapshot.
export const isSkipperReport = (report: AnyReport): report is Record<string, unknown> =>
  Boolean(report && 'skipper' == report.type)

// Provenance, not the snapshot: a failed generation drops guided_report, and only the
// auto-launched report belongs under Automated Reports.
export const isSkipperGuidedReport = (report: AnyReport): boolean =>
  isSkipperReport(report) && 'auto_launch' != report.origin

export const isSkipperGuidedDraftReport = (report: AnyReport): boolean =>
  Boolean(report && 'draft' == report.status && isSkipperGuidedReport(report))

// A guided row is unsettled until its generation reaches a terminal outcome — before dispatch
// ('draft') and while it runs ('generating'). The popup withholds the same editable and shareable
// content across both: there is nothing to share yet, and an edit racing the generation's own
// writes to the row is refused on a stale edit stamp.
export const isSkipperGuidedUnsettledReport = (report: AnyReport): boolean =>
  Boolean(isSkipperGuidedReport(report) && ['draft', 'generating'].includes(report?.status as string))

// The guided-reports feature is "on" (purchased) when it is neither hidden nor in
// an upsell state (available-to-purchase / locked) — the same condition under
// which SkipperGuidedReportButton renders its Create button.
export const isSkipperReportPurchased = (
  feature?: { hidden?: boolean; available?: boolean; disabled?: boolean } | null
): boolean => Boolean(feature && !feature.hidden && !feature.available && !feature.disabled)

export const readResolvedSpec = (report: AnyReport): ResolvedSpec => {
  if (!isSkipperReport(report)) return { ...EMPTY }

  // A failed generation drops the snapshot while the report stays a Skipper Report
  const guided = asRecord(report.guided_report)
  const spec = asRecord(guided.resolved_report_spec)
  const metadata = asRecord(spec.metadata)

  const rqs = toArray(spec.research_questions).map(rq => {
    const item = rq as { text?: string }
    return item && 'object' == typeof item ? item.text || '' : String(rq)
  })

  return {
    objectives: toArray(spec.objectives).map(String),
    researchQuestions: rqs.filter(Boolean),
    kpis: toArray(spec.kpis).map(String),
    confidenceLevel: (spec.confidence_level as number) ?? null,
    methodology: (metadata.methodology as string) ?? null
  }
}

// The plan's derived panel string. Absent on pre-plan reports, which keep the resolved-spec
// rows — no report_prompt fallback, since on that path the field is the raw state.md.
export const readDisplayMd = (report: AnyReport): string => {
  if (!isSkipperReport(report)) return ''

  const displayMd = asRecord(report.guided_report).display_md
  return 'string' == typeof displayMd ? displayMd : ''
}
